/**
 * SSH终端核心逻辑
 * 基于xterm.js实现SSH Web终端
 */

import { Injectable } from '@tanbo/vue-di-plugin'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebglAddon } from '@xterm/addon-webgl'
import '@xterm/xterm/css/xterm.css'
import { Subject } from 'rxjs'

const WS_URL = 'ws://localhost:9001/ws?role=master'

// 连接状态枚举
export enum TerminalState {
  START = 1,
  CONNECTING = 2,
  CONNECTED = 3,
  OPENING = 4,
  OPENED = 5,
  CLOSING = 6,
  CLOSED = 7,
  DISCONNECTING = 8,
  DISCONNECTED = 9,
  RECONNECTING = 10
}

// 消息严重程度
export enum Severity {
  NORMAL = 1,
  WARNING = 2,
  ERROR = 3
}

@Injectable()
export class SshTerminalService {
  private term: Terminal | null = null
  private socket: WebSocket | null = null
  private fitAddon: FitAddon | null = null
  private state: TerminalState = TerminalState.START
  private rows = 24
  private cols = 80
  private resize: number | undefined
  private readonly starttime = new Date()
  private msgtimeout: number | undefined
  private containerElement: HTMLElement | null = null
  private wsUrl: string = ''

  /** 状态变化发布者 */
  stateChangeSubject = new Subject<TerminalState>()

  /** 错误事件发布者 */
  errorSubject = new Subject<any>()

  /**
   * 初始化终端
   */
  initTerminal(containerElement: HTMLElement): void {
    // 从容器元素中查找终端容器
    const terminalContainer = containerElement.querySelector('.ssh-terminal-container') as HTMLElement
    if (!terminalContainer) {
      console.error('找不到终端容器')
      return
    }

    this.containerElement = containerElement

    // 创建终端实例
    this.term = new Terminal(this.getTerminalSettings())
    this.term.open(terminalContainer)

    // 加载适配插件
    this.fitAddon = new FitAddon()
    this.term.loadAddon(this.fitAddon)

    // 尝试加载WebGL加速
    let loadedWebgl = false
    try {
      if (this.detectWebgl()) {
        const webglAddon = new WebglAddon()
        this.term.loadAddon(webglAddon)
        loadedWebgl = true
      }
    } catch (e) {
      console.warn('WebGL加载失败，使用普通渲染器', e)
    }

    if (!loadedWebgl) {
      console.warn('webgl-addon loading failed, falling back to regular dom renderer')
    }

    // 监听终端大小变化
    this.term.onResize((size) => {
      this.rows = size.rows
      this.cols = size.cols
      if (this.state === TerminalState.OPENED && this.socket) {
        const setWinSizeRequest = {
          cmd: 'set_win_size',
          type: 'req',
          ws_row: size.rows,
          ws_col: size.cols,
          ws_xpixel: 0,
          ws_ypixel: 0
        }
        this.socket.send(JSON.stringify(setWinSizeRequest))
      }
    })

    // 监听窗口大小变化
    window.addEventListener('resize', () => {
      clearTimeout(this.resize)
      this.resize = window.setTimeout(() => {
        this.fitAddon?.fit()
      }, 250)
    })

    // 状态栏点击隐藏
    const statusBar = this.containerElement?.querySelector('.ssh-status-bar') as HTMLElement
    statusBar?.addEventListener('click', () => this.hideMsg())
  }

  /**
   * 开始SSH连接
   * @param wsUrl WebSocket 连接地址（完整的 URL，包含 worker_id 和 auth 参数）
   */
  startConnection(wsUrl: string): void {
    console.log('准备连接 WebSocket:', wsUrl)
    this.wsUrl = wsUrl

    this.socket = new WebSocket(wsUrl)
    this.socket.binaryType = 'arraybuffer'
    this.socket.onopen = () => {
      console.log('WebSocket 连接已打开')
      this.runTerminal()
    }
    this.socket.onclose = (event) => this.onWsClose(event)
    this.socket.onerror = (error) => this.onWsError(error)
    this.updateState(TerminalState.CONNECTING)
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    this.clearEvents()
    this.socket?.close()
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.state === TerminalState.OPENED || this.state === TerminalState.CONNECTED
  }


  /**
   * 显示状态消息
   */
  showMsg(message: string, timeout?: number, severity?: Severity): void {
    if (!this.containerElement) return
    const statusBar = this.containerElement.querySelector('.ssh-status-bar') as HTMLElement
    if (!statusBar) return

    clearTimeout(this.msgtimeout)

    statusBar.classList.remove('normal', 'warning', 'error')
    statusBar.textContent = message

    severity = severity || Severity.NORMAL

    switch (severity) {
      case Severity.NORMAL:
        statusBar.classList.add('normal')
        break
      case Severity.WARNING:
        statusBar.classList.add('warning')
        break
      case Severity.ERROR:
        statusBar.classList.add('error')
        break
    }

    statusBar.classList.add('open')

    if (timeout !== 0) {
      this.msgtimeout = window.setTimeout(() => this.hideMsg(), timeout || 1500)
    }
  }

  /**
   * 运行终端
   */
  private runTerminal(): void {
    if (!this.term || !this.socket) {
      console.error('终端或WebSocket未初始化', { term: !!this.term, socket: !!this.socket })
      return
    }

    console.log('连接成功建立!')
    console.log('当前状态:', this.state, '目标状态:', TerminalState.CONNECTED)
    this.updateState(TerminalState.CONNECTED)

    this.socket.onmessage = (event) => {
      if (!this.term) return

      console.log('收到 WebSocket 消息, 当前状态:', this.state, '消息类型:', typeof event.data)

      if (this.state === TerminalState.OPENED) {
        try {
          const cmd = JSON.parse(event.data)
          console.log('已打开状态收到命令:', cmd)

          if (cmd.cmd === 'close_pty' && cmd.type === 'req') {
            console.log('Worker请求关闭PTY!')
            this.term.write('\x1B[38;5;216m受控端PTY已退出! The remote PTY has exited!\x1B[0m\r\n')
            this.updateState(TerminalState.CONNECTED)
            const closePtyResp = {
              cmd: 'close_pty',
              type: 'resp',
              error: 0,
              reason: ''
            }
            this.socket?.send(JSON.stringify(closePtyResp))
          } else if (cmd.cmd === 'set_win_size' && cmd.type === 'resp') {
            if (cmd.error !== 0) {
              console.log('远程worker设置窗口大小失败! ' + cmd.reason)
            } else {
              console.log('远程worker设置窗口大小成功!')
            }
          } else {
            console.log('未知命令消息: ' + event.data)
          }
        } catch (e) {
          // 不是JSON，是PTY数据
          const ptyData = new TextDecoder().decode(new Uint8Array(event.data))
          this.term.write(ptyData)
        }
      } else if (this.state === TerminalState.OPENING) {
        try {
          const cmd = JSON.parse(event.data)
          console.log('打开中状态收到响应:', cmd)
          if (cmd.cmd === 'open_pty' && cmd.type === 'resp') {
            if (cmd.error === 0) {
              console.log('open_pty 成功!')
              this.updateState(TerminalState.OPENED)

              requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                  this.term?.focus()
                  this.fitAddon?.fit()
                })
              )
            } else {
              this.term.clear()
              this.term.write('\x1B[38;5;216mopen_pty failed! ' + cmd.reason + '!\x1B[0m\r\n')
              this.updateState(TerminalState.DISCONNECTED)
              console.log('open_pty failed! ' + cmd.reason)
            }
          } else {
            this.socket?.close()
          }
        } catch (e) {
          console.log('解析命令消息失败: ' + event.data + ' ' + e)
        }
      }
    }

    // 监听终端输入
    this.term.onData((data) => {
      if (this.state === TerminalState.OPENED && this.socket) {
        const encoder = new TextEncoder()
        const binaryData = encoder.encode(data)
        this.socket.send(binaryData)
      }
    })

    // 从 wsUrl 解析 worker_id 和 auth
    const urlParams = new URLSearchParams(this.wsUrl.split('?')[1])
    const workerId = urlParams.get('w') || ''
    const auth = urlParams.get('tk') || ''

    // 发送open_pty请求
    const openPtyReq = {
      cmd: 'open_pty',
      type: 'req',
      worker_id: workerId,
      auth: auth,
      shell: 'bash',
      ws_row: this.rows,
      ws_col: this.cols,
      ws_xpixel: 0,
      ws_ypixel: 0
    }
    console.log('发送 open_pty 请求:', openPtyReq)
    this.updateState(TerminalState.OPENING)
    this.socket.send(JSON.stringify(openPtyReq))
    console.log('open_pty 请求已发送')
  }

  /**
   * 更新连接状态
   */
  private updateState(newState: TerminalState, msg?: string, code?: number): void {
    let timeout: number | undefined
    let severity: Severity | undefined
    let message = ''

    switch (newState) {
      case TerminalState.CONNECTING:
        message = '正在连接...'
        timeout = 0
        severity = Severity.WARNING
        break
      case TerminalState.CONNECTED:
        message = '已连接'
        break
      case TerminalState.OPENING:
        message = '正在打开终端...'
        break
      case TerminalState.OPENED:
        message = '终端已打开'
        break
      case TerminalState.DISCONNECTING:
        message = '正在断开连接...'
        timeout = 0
        severity = Severity.WARNING
        break
      case TerminalState.RECONNECTING:
        message = '正在重新连接...'
        timeout = 0
        severity = Severity.WARNING
        break
      case TerminalState.DISCONNECTED:
        message = '连接已断开'
        timeout = 0
        severity = Severity.ERROR
        break
      default:
        throw new Error('unknown state')
    }

    const msgArr: string[] = []
    if (msg) msgArr.push(msg)
    if (code !== undefined) msgArr.push(`Code: ${code}`)
    if (msgArr.length > 0) {
      message += ` (${msgArr.join(', ')})`
    }

    this.state = newState
    this.stateChangeSubject.next(newState)
    this.showMsg(message, timeout, severity)
  }

  /**
   * WebSocket关闭事件
   */
  private onWsClose(event: CloseEvent): void {
    console.log('连接断开: ' + event.code.toString() + '-' + event.reason.toString())
    this.updateState(TerminalState.DISCONNECTED, event.reason, event.code)
  }

  /**
   * WebSocket错误事件
   */
  private onWsError(error: Event): void {
    console.error('连接发生错误:', error)
    this.errorSubject.next(error)
  }

  /**
   * 清除事件监听
   */
  private clearEvents(): void {
    if (this.term) {
      this.term.onResize(() => {})
      this.term.onData(() => {})
    }
  }

  /**
   * 隐藏状态消息
   */
  private hideMsg(): void {
    if (!this.containerElement) return
    const statusBar = this.containerElement.querySelector('.ssh-status-bar') as HTMLElement
    if (!statusBar) return

    clearTimeout(this.msgtimeout)
    statusBar.classList.remove('open')
  }

  /**
   * 获取终端设置
   */
  private getTerminalSettings(): any {
    const res: any = {}
    const settings = ['fontSize', 'letterSpacing', 'lineHeight']
    if (localStorage) {
      settings.forEach((setting) => {
        const val = localStorage.getItem('ssh-xterm-' + setting)
        if (val !== undefined && val !== null) {
          res[setting] = val
        }
      })
      localStorage.removeItem('ssh-xterm-fontFamily')
    }
    delete res.fontFamily
    return res
  }

  /**
   * 检测WebGL支持
   */
  private detectWebgl(): boolean {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl2', {
        failIfMajorPerformanceCaveat: true,
      })
      return !!gl
    } catch (e) {
      return false
    }
  }
}

